import create, { StoreApi, UseBoundStore } from "zustand";
import { persist } from "zustand/middleware";

import { useConfigurationStore } from "@stores";

import { initialSettings, initialTableSettings } from "@constants/userStoreInitialSettings";

import { QueryFilter } from "types/common";
import { Pagination as IPagination } from "types/responses/index";

export type PageSettings = {
  sorting?: string[];
  filters?: object | QueryFilter[];
  pagination?: IPagination;
  // The following properties are used by the training matrix component
  showCourseScore?: boolean;
  showCourseProgress?: boolean;
  focusedOn?: string;
  // The following properties are used by the unit matrix component
  focusedOnOption?: string;
  // The following properties are used in my courses view
  isCategoryView?: boolean;
};

type Settings = Record<string, PageSettings>;

type UserPreferencesProps = {
  settings: Settings;
  setSettings: (settings: PageSettings, path: string) => void;
  resetSettings: () => void;
};

const userStores: Record<string, StoreApi<UserPreferencesProps>> = {};

const useUserPreferencesStore = (): UseBoundStore<
  UserPreferencesProps,
  StoreApi<UserPreferencesProps>
> => {
  const { getState: getConfigurationState } = useConfigurationStore;
  const userId = getConfigurationState().userProfileData?.id;
  const storeKey = `user-store-${userId || "default"}`;

  const persistSettings = persist<UserPreferencesProps>(
    (set, get) => {
      return {
        settings: { ...initialSettings, ...initialTableSettings },
        setSettings: (newSettings: PageSettings, path: string): void => {
          const updatedSettings = { ...get().settings, [path]: newSettings };
          set({ settings: updatedSettings });
        },
        resetSettings: (): void => {
          set({ settings: { ...initialTableSettings } });
        },
      };
    },
    {
      name: storeKey, // Unique store key for each user
      getStorage: () => localStorage,
    },
  );

  if (!userStores[storeKey]) {
    userStores[storeKey] = create<UserPreferencesProps>(persistSettings);
  }

  return userStores[storeKey] as UseBoundStore<
    UserPreferencesProps,
    StoreApi<UserPreferencesProps>
  >;
};

export default useUserPreferencesStore;
